import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import Layout from '../../components/Layout/Layout';
import { Card, Spinner } from '../../components/UI';
import { dashboardAPI, callLogsAPI, reportsAPI, pipelineAPI, usersAPI, leadsAPI } from '../../services/api';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineFilter } from 'react-icons/hi';

// Lead status enum (matches the runtime Lead schema in the backend)
const LEAD_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'cold', label: 'Cold' },
  { value: 'warm', label: 'Warm' },
  { value: 'hot', label: 'Hot' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
  { value: 'lost', label: 'Lost' },
  { value: 'not_connected', label: 'Not Connected' },
];

const LEAD_PRIORITIES = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const DATE_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'custom', label: 'Custom' },
];

const getDateParams = (range, startDate, endDate) => {
  const params = {};
  if (range === 'today') {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    params.startDate = today.toISOString();
  } else if (range === 'week') {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0);
    params.startDate = d.toISOString();
  } else if (range === 'month') {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
    params.startDate = d.toISOString();
  } else if (range === 'quarter') {
    const d = new Date(); const q = Math.floor(d.getMonth() / 3); d.setMonth(q * 3); d.setDate(1); d.setHours(0, 0, 0, 0);
    params.startDate = d.toISOString();
  } else if (range === 'custom') {
    if (startDate) { const s = new Date(startDate); s.setHours(0, 0, 0, 0); params.startDate = s.toISOString(); }
    if (endDate) { const e = new Date(endDate); e.setHours(23, 59, 59, 999); params.endDate = e.toISOString(); }
  }
  return params;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

/**
 * Analytics — currently shows only the "Call Duration Analytics" section
 * (taken from the CEO Dashboard, which is commented out in the sidebar).
 * More analytics sections can be added under this page later.
 */
const Analytics = () => {
  const [range, setRange] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const dateParams = useMemo(() => getDateParams(range, startDate, endDate), [range, startDate, endDate]);
  const queryParams = useMemo(() => ({ range, ...dateParams }), [range, dateParams]);

  // Call duration data (same sources as the CEO Dashboard's Call Duration Analytics card)
  const { data: statsData } = useQuery({
    queryKey: ['analyticsCallStats', range, startDate, endDate],
    queryFn: async () => { try { const r = await dashboardAPI.getStats(queryParams); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: callStats } = useQuery({
    queryKey: ['analyticsCallLogStats', range, startDate, endDate],
    queryFn: async () => { try { const r = await callLogsAPI.getStatistics({ source: 'app', ...queryParams }); return r.data?.data || {}; } catch { return {}; } },
  });

  const stats = statsData || {};
  const callAnalytics = stats.callAnalytics || {};
  const callTypeData = stats.callTypeData || {};

  // Lead Ageing Report — leads grouped by days in the system / current stage.
  // Filters: stage, assigned user, status, source, priority.
  const [ageingStage, setAgeingStage] = useState('all');
  const [ageingStatus, setAgeingStatus] = useState('all');
  const [ageingSource, setAgeingSource] = useState('all');
  const [ageingPriority, setAgeingPriority] = useState('all');
  const [ageingEmployee, setAgeingEmployee] = useState('all');

  const ageingFilterParams = useMemo(() => ({
    ...queryParams,
    ...(ageingStage !== 'all' ? { stage: ageingStage } : {}),
    ...(ageingStatus !== 'all' ? { status: ageingStatus } : {}),
    ...(ageingSource !== 'all' ? { source: ageingSource } : {}),
    ...(ageingPriority !== 'all' ? { priority: ageingPriority } : {}),
    ...(ageingEmployee !== 'all' ? { employee: ageingEmployee } : {}),
  }), [queryParams, ageingStage, ageingStatus, ageingSource, ageingPriority, ageingEmployee]);

  const { data: ageingData, isLoading: ageingLoading } = useQuery({
    queryKey: ['leadAgeing', ageingFilterParams],
    queryFn: async () => { try { const r = await reportsAPI.getLeadAgeing(ageingFilterParams); return r.data?.data || {}; } catch { return {}; } },
  });
  const ageing = ageingData || {};
  const ageingSummary = ageing.summary || {};
  // Normalise the bucket list: counts must be numbers or recharts renders empty bars
  const ageDistribution = Array.isArray(ageing.distribution)
    ? ageing.distribution.map((d, i) => ({
        label: String(d.label ?? d.name ?? `Bucket ${i + 1}`),
        count: Number(d.count ?? d.value) || 0,
        color: d.color || '#6B7280',
      }))
    : [];
  const stageAgeing = ageing.stageAgeing || [];

  // Filter options for the Lead Ageing Report
  const filterSelectCls = "px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none bg-white";

  const { data: stagesData } = useQuery({
    queryKey: ['analyticsPipelineStages'],
    queryFn: async () => { try { const r = await pipelineAPI.getAll(); return r.data?.data || []; } catch { return []; } },
    staleTime: 5 * 60 * 1000,
  });
  const pipelineStages = Array.isArray(stagesData) ? stagesData : (stagesData?.stages || []);

  const { data: usersData } = useQuery({
    queryKey: ['analyticsFilterUsers'],
    queryFn: async () => { try { const r = await usersAPI.getAll({ limit: 100 }); return r.data?.data?.users || r.data?.data || []; } catch { return []; } },
    staleTime: 5 * 60 * 1000,
  });
  const filterUsers = Array.isArray(usersData) ? usersData : [];

  const { data: sourcesData } = useQuery({
    queryKey: ['analyticsFilterSources'],
    queryFn: async () => { try { const r = await leadsAPI.getSources(); return r.data?.data || []; } catch { return []; } },
    staleTime: 5 * 60 * 1000,
  });
  const leadSources = Array.isArray(sourcesData) ? sourcesData : [];

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <HiOutlineChartBar className="w-5 h-5 text-primary-600" />
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Analytics</h1>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">Call duration & lead ageing insights</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none"
            >
              {DATE_RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            {range === 'custom' && (
              <>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none" />
              </>
            )}
          </div>
        </div>

        {/* Call Duration Analytics — only section of the Analytics page */}
        <Card padding="p-3 sm:p-4 lg:p-5" className="!shadow-[0_-2px_10px_-2px_rgba(0,0,0,0.06),0_6px_14px_-2px_rgba(0,0,0,0.10)] hover:!shadow-[0_-2px_10px_-2px_rgba(0,0,0,0.06),0_12px_24px_-4px_rgba(0,0,0,0.16)]">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Call Duration Analytics</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="text-center p-3 bg-green-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
              <div className="text-lg sm:text-xl font-bold text-green-600">{formatDuration(callAnalytics.averageDuration || callStats?.averageDuration || 0)}</div>
              <div className="text-xs text-gray-500 mt-1">Avg Duration</div>
              <div className="text-xs text-green-500">per connected call</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
              <div className="text-lg sm:text-xl font-bold text-blue-600">{formatDuration(callAnalytics.totalTalkTime || callStats?.totalDuration || 0)}</div>
              <div className="text-xs text-gray-500 mt-1">Total Talk Time</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
              <div className="text-lg sm:text-xl font-bold text-purple-600">{formatDuration(callAnalytics.maxDuration || 0)}</div>
              <div className="text-xs text-gray-500 mt-1">Longest Call</div>
            </div>
          </div>
          {/* Call type breakdown — always visible (all 6 points of the section) */}
          <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100">
            <div className="text-center">
              <div className="text-sm font-semibold text-blue-600">{callTypeData.outgoing?.count || 0}</div>
              <div className="text-xs text-gray-500">Outgoing</div>
              <div className="text-xs text-green-500">{callTypeData.outgoing?.connected || 0} connected</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-semibold text-indigo-600">{callTypeData.incoming?.count || 0}</div>
              <div className="text-xs text-gray-500">Incoming</div>
              <div className="text-xs text-green-500">{callTypeData.incoming?.connected || 0} connected</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-semibold text-red-600">{callTypeData.missed?.count || 0}</div>
              <div className="text-xs text-gray-500">Missed</div>
            </div>
          </div>
        </Card>

        {/* Lead Ageing Report — groups leads by how long they have remained
            in the system or in their current pipeline stage */}
        <Card padding="p-3 sm:p-4 lg:p-5" className="!shadow-[0_-2px_10px_-2px_rgba(0,0,0,0.06),0_6px_14px_-2px_rgba(0,0,0,0.10)] hover:!shadow-[0_-2px_10px_-2px_rgba(0,0,0,0.06),0_12px_24px_-4px_rgba(0,0,0,0.16)]">
          <div className="flex items-start gap-2 mb-3 sm:mb-4">
            <HiOutlineClock className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Lead Ageing Report</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Groups leads by how long they have remained in the system or in their current stage
              </p>
            </div>
          </div>

          {/* Filters — every input sits in ONE line (no wrapping); on small
              screens the row scrolls sideways instead of breaking apart */}
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 overflow-x-auto hide-scrollbar">
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 font-medium flex-shrink-0">
              <HiOutlineFilter className="w-3.5 h-3.5" /> Filters:
            </span>
            <select value={ageingStage} onChange={(e) => setAgeingStage(e.target.value)} className={`${filterSelectCls} flex-shrink-0 w-36`}>
              <option value="all">All Stages</option>
              {pipelineStages.map((s) => (
                <option key={s._id || s.key} value={s.key}>{s.name}</option>
              ))}
            </select>
            <select value={ageingEmployee} onChange={(e) => setAgeingEmployee(e.target.value)} className={`${filterSelectCls} flex-shrink-0 w-36`}>
              <option value="all">All Users</option>
              {filterUsers.map((u) => (
                <option key={u._id} value={u._id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</option>
              ))}
            </select>
            <select value={ageingStatus} onChange={(e) => setAgeingStatus(e.target.value)} className={`${filterSelectCls} flex-shrink-0 w-36`}>
              <option value="all">All Statuses</option>
              {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select value={ageingSource} onChange={(e) => setAgeingSource(e.target.value)} className={`${filterSelectCls} flex-shrink-0 w-36`}>
              <option value="all">All Sources</option>
              {leadSources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={ageingPriority} onChange={(e) => setAgeingPriority(e.target.value)} className={`${filterSelectCls} flex-shrink-0 w-36`}>
              <option value="all">All Priorities</option>
              {LEAD_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {(ageingStage !== 'all' || ageingStatus !== 'all' || ageingSource !== 'all' || ageingPriority !== 'all' || ageingEmployee !== 'all') && (
              <button
                onClick={() => {
                  setAgeingStage('all'); setAgeingStatus('all'); setAgeingSource('all');
                  setAgeingPriority('all'); setAgeingEmployee('all');
                }}
                className="flex-shrink-0 ml-auto px-2.5 py-1.5 text-xs font-medium rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition"
              >
                Clear filters
              </button>
            )}
          </div>

          {ageingLoading ? (
            <div className="py-12 flex justify-center"><Spinner /></div>
          ) : (
            <>
              {/* Summary tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="text-center p-3 bg-gray-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
                  <div className="text-lg sm:text-xl font-bold text-gray-800">{ageingSummary.totalLeads || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Total Leads</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
                  <div className="text-lg sm:text-xl font-bold text-green-600">{ageingSummary.fresh || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Fresh (0-7 days)</div>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
                  <div className="text-lg sm:text-xl font-bold text-amber-600">{ageingSummary.aging || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Aging (8-30 days)</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
                  <div className="text-lg sm:text-xl font-bold text-red-600">{ageingSummary.stale || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Stale (30+ days)</div>
                  <div className="text-xs text-red-500">{ageingSummary.stalePercent || '0.0'}% of leads</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-xl shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.06),0_3px_6px_-1px_rgba(0,0,0,0.08)] hover:shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.07),0_8px_12px_-2px_rgba(0,0,0,0.13)] transition-shadow duration-200">
                  <div className="text-lg sm:text-xl font-bold text-blue-600">{ageingSummary.avgAgeDays || 0}d</div>
                  <div className="text-xs text-gray-500 mt-1">Avg Age</div>
                  <div className="text-xs text-gray-400">oldest {ageingSummary.oldestLeadDays || 0}d</div>
                </div>
              </div>

              {/* Age distribution chart */}
              {ageDistribution.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Age in system</h4>
                  <div className="h-56 sm:h-64 w-full" style={{ minHeight: 224 }}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={224}>
                      <BarChart data={ageDistribution} margin={{ top: 24, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => String(v).replace('More than ', '> ')}
                          interval={0}
                        />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip
                          formatter={(value) => [`${value} lead${value === 1 ? '' : 's'}`, 'Count']}
                          cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56} isAnimationActive={false}>
                          <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: '#6b7280' }} isAnimationActive={false} />
                          {ageDistribution.map((entry) => (
                            <Cell key={entry.label} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Per-stage ageing table */}
              {stageAgeing.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Age in current stage</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th className="py-2 font-medium">Stage</th>
                          <th className="py-2 font-medium text-right">Leads</th>
                          <th className="py-2 font-medium text-right">Avg Age</th>
                          <th className="py-2 font-medium text-right">Fresh</th>
                          <th className="py-2 font-medium text-right">Stale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stageAgeing.map((s) => (
                          <tr key={s.key} className="border-t border-gray-100">
                            <td className="py-2">
                              <span className="inline-flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || '#9CA3AF' }} />
                                <span className="text-gray-800 font-medium">{s.name}</span>
                              </span>
                            </td>
                            <td className="py-2 text-right text-gray-800">{s.total}</td>
                            <td className="py-2 text-right text-gray-600">{s.avgAgeDays}d</td>
                            <td className="py-2 text-right text-green-600">{s.fresh}</td>
                            <td className={`py-2 text-right font-medium ${s.stale > 0 ? 'text-red-600' : 'text-gray-400'}`}>{s.stale}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!ageingSummary.totalLeads && (
                <p className="text-xs text-gray-400 text-center pt-3">No leads found for the selected period.</p>
              )}
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default Analytics;